import { useLazyQuery } from '@apollo/client'
import { ArrowDownIcon, Button, Separator, Spinner } from '@farol-ds/react'
import { HaystackItem, JurisprudenceMetadata } from '@jusbrasil-web/shared-apollo-client/types'
import { useGA4Dispatcher } from '@jusbrasil-web/shared-ga4'
import { GA4ClickEvent, GA4ViewItemListEvent } from '@jusbrasil-web/shared-ga4-events'
import { useEffect, useState } from 'react'

import { DEFAULT_DOCUMENT_LIMIT } from '../../constants'
import { getClusterQueryFilter } from '../../pages/thesis-cluster/cluster-filter'
import { ClusterDocSnippet } from '../cluster-doc-snippet/cluster-doc-snippet'
import {
  ThesisClusterMoreItemsQuery,
  ThesisClusterMoreItemsQueryVariables,
} from './__generated__/lazy-query.graphql'
import styles from './cluster-doc-results.module.scss'
import { ClusterDocResultsFragments } from './fragments'
import { FETCH_MORE_ITEMS } from './lazy-query'

export interface ClusterDocResultsProps {
  documents: HaystackItem[]
  clusterId: string
  hits: number
}

export function ClusterDocResults(props: ClusterDocResultsProps) {
  const { documents, clusterId, hits } = props

  const [results, setResults] = useState<HaystackItem[]>(documents)
  const [hasMoreResults, setHasMoreResults] = useState(results.length < hits)
  const [fetchMoreResults, { loading: loadingMoreResults }] = useLazyQuery<
    ThesisClusterMoreItemsQuery,
    ThesisClusterMoreItemsQueryVariables
  >(FETCH_MORE_ITEMS)
  const { sendGA4Event } = useGA4Dispatcher()

  useEffect(() => {
    sendGA4Event(
      new GA4ViewItemListEvent({
        renders: results.map((document, idx) => ({
          item_variant: idx < DEFAULT_DOCUMENT_LIMIT ? 'static_reference' : 'show_more',
          item_name: 'cluster_theses_jurisprudence',
          item_brand: 'result_snippet',
          item_id: `JURISPRUDENCIA:${document.data.docId}`,
        })),
      })
    )
  }, [results, sendGA4Event])

  const handleLoadMore = () => {
    sendGA4Event(
      new GA4ClickEvent({
        componentName: 'cluster_theses_jurisprudence',
        componentClass: 'result_snippet',
        selectedOption: 'show_more',
        resourceId: clusterId,
      })
    )
    fetchMoreResults({
      variables: {
        filters: getClusterQueryFilter(clusterId),
        offset: results.length,
        limit: null,
      },
    }).then((res) => {
      const newItems = (res.data?.root?.searchHaystack?.items ?? []) as HaystackItem[]
      setResults([...results, ...newItems])
      setHasMoreResults(results.length < hits)
    })
  }

  return (
    <div className={styles.container}>
      <div className={styles.resultsContainer}>
        {results.map((document, idx) => (
          <ClusterDocSnippet
            key={idx}
            document={document.data as JurisprudenceMetadata}
            isLazyLoadedSnippet={idx >= DEFAULT_DOCUMENT_LIMIT}
          />
        ))}
      </div>
      {loadingMoreResults && <Spinner />}
      {hasMoreResults && (
        <div className={styles.loadMoreContainer}>
          <Separator orientation="horizontal" className={styles.separator} />
          <Button
            data-testid="load-more-button"
            disabled={loadingMoreResults}
            onClick={handleLoadMore}
            className={styles.loadMoreButton}
            rightIcon={<ArrowDownIcon />}
          >
            Carregar mais julgados
          </Button>
        </div>
      )}
    </div>
  )
}

ClusterDocResults.fragments = ClusterDocResultsFragments
